import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { tap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { RoteiroWizardStore } from './wizard.store';
import { VisitPlanService } from '../../../core/services/visit-plan.service';
import { getStepNumber, getStepRoute } from '../models/visit-plan';

export const wizardResolver: ResolveFn<boolean> = (route) => {
  const router = inject(Router);
  const id = route.paramMap.get('id');
  if (!id) return true;

  const visitPlanId = parseInt(id, 10);
  if (isNaN(visitPlanId)) return true;

  const store = inject(RoteiroWizardStore);
  const service = inject(VisitPlanService);

  const getTargetChildRoute = () => {
    const childRoute = route.firstChild;
    return childRoute?.routeConfig?.path ?? '';
  };

  const redirectToCorrectStep = (stepNumber: number) => {
    const currentChildRoute = getTargetChildRoute();
    const targetRoute = getStepRoute(stepNumber);

    if (!currentChildRoute && targetRoute) {
      router.navigate([`/roteirizacao/editar/${id}/${targetRoute}`]);
    }
  };

  if (store.visitPlanId() === visitPlanId) {
    redirectToCorrectStep(store.currentStep());
    return true;
  }

  return service.getVisitPlanDetails(visitPlanId).pipe(
    tap((visitPlan) => {
      store.setFromVisitPlan(visitPlan);
      redirectToCorrectStep(getStepNumber(visitPlan.lastStep));
    }),
    map(() => true),
    catchError(() => {
      router.navigate(['/roteirizacao']);
      return of(false);
    })
  );
};

/**
 * Resolver da rota `/roteirizacao/novo`. Limpa o estado do wizard ao iniciar uma
 * criação nova. Necessário porque o EnvironmentInjector da rota não é reciclado
 * entre navegações SPA: a mesma instância de `RoteiroWizardStore` sobrevive e
 * carregaria o estado da criação anterior. Roda só na ativação da rota-pai (não
 * entre os steps-filhos), preservando o `restoreStateFromStore` da navegação
 * interna do wizard. Simétrico ao `wizardResolver`, que popula o estado na edição.
 */
export const newRoteiroResolver: ResolveFn<boolean> = () => {
  inject(RoteiroWizardStore).reset();
  return true;
};
